import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PredefinedBasket } from '../../../types/Baskets';
import { colors } from '../../../colors';
import AssetIcon from '../../../components/AssetIcon';
import ExpandableModal, {
  ExpandableModalChildProps,
} from '../../../components/ExpandableModal';
import { BasketItemContext, useBasketItemContext } from './context';
import InputField from '../../../components/Form/InputField';
import { GetTopAssetsByVolumeInfo } from '../../../types/Bot';
import ColoredCheckbox from '../../../components/ColoredCheckbox';
import Tag from '../Tag';
import {
  useAddBasketMutation,
  useCalculateCurrentSingleProfitCalculationQuery,
} from '../../../store/services/api';
import { Exchange } from '../../../types/exchange';
import { Validations } from '../../../components/Form';
import { useSelector } from 'react-redux';
import { selectIsDev, selectUserId } from '../../../store/slices/user';
import LoadingIcons from 'react-loading-icons';

const MINIMUM_BOT_BALANCE = 50;

interface Props {
  item: PredefinedBasket;
  refresh: () => void;
  activeBasket: string | undefined;
  setActiveBasket: React.Dispatch<React.SetStateAction<string | undefined>>;
  assetInfo: Record<string, GetTopAssetsByVolumeInfo[]>;
  parentRef: React.RefObject<HTMLDivElement>;
  balances: Record<string, Record<string, number>> | undefined;
  credits: number | undefined;
}

const BasketItem = (props: Props) => {
  const {
    item,
    refresh,
    activeBasket,
    setActiveBasket,
    assetInfo,
    balances,
    credits,
    parentRef,
  } = props;

  return (
    <BasketItemContext
      activeBasket={activeBasket}
      setActiveBasket={setActiveBasket}
    >
      <ExpandableModal
        key={item.friendlyName}
        parentRef={parentRef}
        supportScrolling
      >
        <BasketItemContent
          item={item}
          assetInfo={assetInfo}
          refresh={refresh}
          balances={balances}
          credits={credits}
        />
      </ExpandableModal>
    </BasketItemContext>
  );
};

type BasketItemContentProps = ExpandableModalChildProps & {
  item: PredefinedBasket;
  assetInfo: Record<string, GetTopAssetsByVolumeInfo[]>;
  refresh: () => void;
  balances: Record<string, Record<string, number>> | undefined;
  credits: number | undefined;
};

const BasketItemContent = forwardRef<HTMLDivElement, BasketItemContentProps>(
  (props, childRef) => {
    const { item, assetInfo, balances, credits, refresh } = props;
    const { handleModalOpen, handleModalClose, style, isModalOpen } = props;
    const { activeBasket, setActiveBasket } = useBasketItemContext();
    const isDev = useSelector(selectIsDev);
    const userId = useSelector(selectUserId);

    const [addBasket] = useAddBasketMutation();
    const [success, setSuccess] = useState<boolean | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    const handleBasketClick = useCallback(() => {
      if (activeBasket === item.friendlyName) {
        return;
      } else {
        setActiveBasket(item.friendlyName);
        handleModalOpen?.();
      }
    }, [activeBasket, handleModalOpen, item.friendlyName, setActiveBasket]);

    const baseAssets = useMemo(() => {
      return item.baseAssets.split(',').sort((a, b) => {
        return (assetInfo[b] ? 1 : 0) - (assetInfo[a] ? 1 : 0);
      });
    }, [assetInfo, item.baseAssets]);

    const [basketConfig, setBasketConfig] = useState(() => {
      return baseAssets.reduce<
        Record<
          string,
          {
            stableBalance: string;
            assetBalance: string;
            isIncluded: boolean;
            isBalanceLocked: boolean;
            quoteAsset: string;
            exchange: Exchange;
            latestPrice: number;
          }
        >
      >((acc, curr) => {
        const info = assetInfo[curr];

        const possibleQuoteAssets = info
          ?.map((o) => o.quoteAsset)
          .filter((o, i, s) => s.findIndex((so) => so === o) === i);

        const possibleExchanges = info
          ?.filter((o) => o.quoteAsset === possibleQuoteAssets[0])
          .map((o) => o.exchange)
          .filter((o, i, s) => s.findIndex((so) => so === o) === i);

        acc[curr] = {
          stableBalance: MINIMUM_BOT_BALANCE.toString(),
          assetBalance: '0',
          isIncluded: !!assetInfo[curr],
          isBalanceLocked: false,
          quoteAsset: possibleQuoteAssets?.[0],
          exchange: possibleExchanges?.[0],
          latestPrice: 0,
        };

        return acc;
      }, {});
    });

    const amountOfBots = useMemo(() => {
      return Object.values(basketConfig).reduce(
        (acc, curr) => acc + (curr.isIncluded ? 1 : 0),
        0,
      );
    }, [basketConfig]);

    const updateBasketConfigBotBalances = useCallback(
      (value: string) => {
        const newBalance = parseInt(value);

        // Don't use amountOfBots
        // Use amountOfBots - non included bots
        const includedBotsWithBalance = Object.values(basketConfig).reduce(
          (acc, curr) =>
            acc + (curr.isIncluded && !curr.isBalanceLocked ? 1 : 0),
          0,
        );

        const usableNewBalance =
          newBalance -
          Object.values(basketConfig).reduce(
            (acc, curr) =>
              acc +
              (curr.isIncluded && curr.isBalanceLocked
                ? parseInt(curr.stableBalance)
                : 0),
            0,
          );

        const balancePerBot = Math.max(
          0,
          usableNewBalance / includedBotsWithBalance,
        ).toFixed(0);

        setBasketConfig((prev) => {
          const updated = { ...prev };

          Object.values(updated).forEach((config) => {
            if (config.isIncluded && !config.isBalanceLocked) {
              config.stableBalance = value.length === 0 ? '0' : balancePerBot;
            }
          });

          return updated;
        });
      },
      [basketConfig],
    );

    const calculateBasketStableBalance = useCallback(() => {
      return Object.values(basketConfig).reduce<number>((acc, curr) => {
        if (!curr.isIncluded) {
          return acc;
        }

        return acc + parseInt(curr.stableBalance);
      }, 0);
    }, [basketConfig]);

    const [basketBalance, setBasketBalance] = useState<string>(
      calculateBasketStableBalance().toString(),
    );

    const isMinimumBasketBalanceMet = useMemo(() => {
      return parseInt(basketBalance) >= amountOfBots * MINIMUM_BOT_BALANCE;
    }, [amountOfBots, basketBalance]);

    const basketBalanceField = useMemo(() => {
      return {
        label: 'Basket Stable Value',
        value: basketBalance.toString(),
        handleValueChange: (value: string) => {
          if (!!value.length && isNaN(parseInt(value))) {
            return;
          }

          setBasketBalance(value);

          // Update the basket config
          updateBasketConfigBotBalances(value);
        },
        shouldFit: true,
        error: !isMinimumBasketBalanceMet
          ? Validations.MinimumRequired
          : undefined,
      };
    }, [
      basketBalance,
      isMinimumBasketBalanceMet,
      updateBasketConfigBotBalances,
    ]);

    const creditsCost = useMemo(() => {
      return Object.entries(basketConfig).reduce((acc, [baseAsset, config]) => {
        if (!config.isIncluded) {
          return acc;
        }

        const info = assetInfo[baseAsset];
        const isActive = info.some((i) => i.isActive);
        return acc + (isActive ? 1 : 2);
      }, 0);
    }, [assetInfo, basketConfig]);

    const handleIsIncludedChange = useCallback(
      (baseAsset: string, value: boolean) => {
        setBasketConfig((prev) => {
          const updated = { ...prev };
          updated[baseAsset].isIncluded = value;

          // Calculate new balance
          const newBasketStableBalance = Object.values(updated).reduce<number>(
            (acc, curr) => {
              if (!curr.isIncluded) {
                return acc;
              }

              return acc + parseInt(curr.stableBalance || '0');
            },
            0,
          );

          setBasketBalance(newBasketStableBalance.toString());

          return updated;
        });
      },
      [],
    );

    const handleStableBalanceChange = useCallback(
      (baseAsset: string, value: string) => {
        if (!!value && !/^[0-9]+$/.test(value)) {
          return;
        }

        setBasketConfig((prev) => {
          const updated = { ...prev };
          updated[baseAsset].stableBalance = value;

          // Calculate new balance
          const newBasketStableBalance = Object.values(updated).reduce<number>(
            (acc, curr) => {
              if (!curr.isIncluded) {
                return acc;
              }

              return acc + parseInt(curr.stableBalance || '0');
            },
            0,
          );

          setBasketBalance(newBasketStableBalance.toString());

          return updated;
        });
      },
      [],
    );

    const handleAssetBalanceChange = useCallback(
      (baseAsset: string, value: string) => {
        if (!!value && !/^[0-9]+([.,][0-9]*)?$/.test(value)) {
          return;
        }

        setBasketConfig((prev) => {
          const updated = { ...prev };
          updated[baseAsset].assetBalance = value;
          return updated;
        });
      },
      [],
    );

    const handleIsBalanceLockedChange = useCallback(
      (baseAsset: string, value: boolean) => {
        setBasketConfig((prev) => {
          const updated = { ...prev };
          updated[baseAsset].isBalanceLocked = value;
          return updated;
        });
      },
      [],
    );

    const handleExchangeChange = useCallback(
      (baseAsset: string, value: Exchange) => {
        setBasketConfig((prev) => {
          const updated = { ...prev };

          if (updated[baseAsset].isIncluded) {
            updated[baseAsset].exchange = value;
          }

          return updated;
        });
      },
      [],
    );

    const handleLatestPriceChange = useCallback(
      (baseAsset: string, value: number) => {
        setBasketConfig((prev) => {
          const updated = { ...prev };
          updated[baseAsset].latestPrice = value;
          return updated;
        });
      },
      [],
    );

    const handleQuoteAssetChange = useCallback(
      (baseAsset: string, value: string) => {
        setBasketConfig((prev) => {
          const updated = { ...prev };

          if (updated[baseAsset].isIncluded) {
            updated[baseAsset].quoteAsset = value;

            // We might need to change the exchange here
            const exchanges = assetInfo[baseAsset]
              .filter((o) => o.quoteAsset === value)
              .map((o) => o.exchange);

            if (!exchanges.includes(updated[baseAsset].exchange)) {
              handleExchangeChange(baseAsset, exchanges[0]);
            }
          }

          return updated;
        });
      },
      [assetInfo, handleExchangeChange],
    );

    const handleDisableAllButton = useCallback(() => {
      setBasketConfig((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((baseAsset) => {
          updated[baseAsset].isIncluded = false;
        });

        return updated;
      });
    }, []);

    const handleCancel = useCallback(() => {
      setActiveBasket(undefined);
      handleModalClose?.();
    }, [handleModalClose, setActiveBasket]);

    const isSubscribeDisabled = useMemo(() => {
      if (isDev) {
        return false;
      }

      if (!isModalOpen || !!success || isLoading) {
        console.log(1);
        return true;
      }

      // When basket balance is not met
      if (!isMinimumBasketBalanceMet) {
        console.log(2);
        return true;
      }

      // When available credits is less than credits cost
      if (!credits || credits < creditsCost) {
        console.log(3);
        return true;
      }

      // When $50 is not met per included bot
      if (
        Object.values(basketConfig).some(
          (config) =>
            config.isIncluded &&
            parseInt(config.stableBalance) +
              parseFloat(config.assetBalance) * config.latestPrice <
              MINIMUM_BOT_BALANCE,
        )
      ) {
        console.log(4);
        return true;
      }

      // When minimum of 1 bot is included
      if (!Object.values(basketConfig).some((config) => config.isIncluded)) {
        console.log(5);
        return true;
      }

      // When balances are below the exchange balances
      // Partial<Record<Exchange, Record<string, number>>>
      const configBalancesByExchange = Object.entries(basketConfig).reduce<
        Partial<Record<Exchange, Record<string, number>>>
      >((acc, [baseAsset, config]) => {
        if (!config.exchange || !config.quoteAsset || !config.isIncluded) {
          return acc;
        }

        acc[config.exchange] = {
          ...acc[config.exchange],
          [baseAsset]: parseFloat(config.assetBalance),
          [config.quoteAsset]:
            (acc[config.exchange]?.[config.quoteAsset] ?? 0) +
            parseInt(config.stableBalance),
        };

        return acc;
      }, {});

      // Check Each value
      if (
        !balances ||
        Object.keys(balances).length === 0 ||
        Object.entries(configBalancesByExchange).some(
          ([exchange, balancesByAsset]) => {
            return Object.entries(balancesByAsset).some(([asset, balance]) => {
              return balance > (balances[exchange]?.[asset] ?? 0);
            });
          },
        )
      ) {
        console.log(6);
        return true;
      }

      return false;
    }, [
      balances,
      basketConfig,
      credits,
      creditsCost,
      isDev,
      isLoading,
      isMinimumBasketBalanceMet,
      isModalOpen,
      success,
    ]);

    const handleConfirm = useCallback(async () => {
      if (isSubscribeDisabled || !userId) {
        return;
      }

      const postData = {
        userId,
        basket: item.friendlyName,
        bots: Object.entries(basketConfig)
          .filter(([baseAsset, config]) => config.isIncluded)
          .map(([baseAsset, config]) => {
            return {
              baseAsset,
              quoteAsset: config.quoteAsset,
              exchange: config.exchange,
              stableBalance: parseFloat(config.stableBalance),
              assetBalance: parseFloat(config.assetBalance),
            };
          }),
      };

      setIsLoading(true);
      const result = await addBasket(postData);

      if (result.data?.success) {
        refresh();
      }

      setSuccess(result.data?.success);
      setError(result.data?.error);
      setIsLoading(false);

      return null;
    }, [
      addBasket,
      basketConfig,
      isSubscribeDisabled,
      item.friendlyName,
      refresh,
      userId,
    ]);

    // Also used to show success / error message
    const basketBalanceInputMessage = useMemo(() => {
      if (isLoading) {
        return 'Busy subscribing to the basket, this could take a minute...';
      }

      if (!!success) {
        return 'Successfully subscribed to the basket!';
      }

      if (typeof error !== 'undefined') {
        return error;
      }

      return `Minimum Total Balance Required: $${
        amountOfBots * MINIMUM_BOT_BALANCE
      }`;
    }, [amountOfBots, error, isLoading, success]);

    const basketBalanceInputMessageColor = useMemo(() => {
      if (isLoading) {
        return colors.orange;
      }

      if (success) {
        return colors.green;
      }

      if (typeof error !== 'undefined') {
        return colors.red;
      }

      return colors.orange;
    }, [error, isLoading, success]);

    return (
      <div
        ref={childRef}
        style={{
          border: '1px solid rgba(50, 50, 50, 1)',
          borderRadius: 8,
          padding: 16,
          display: 'flex',
          backgroundColor: 'rgba(30, 30, 30, 1)',
          boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
          flexDirection: 'column',
          paddingTop: isModalOpen ? 0 : 8,
          alignItems: 'center',
          // height: 'fit-content',
          transition: 'opacity 0.5s ease',
          opacity: !activeBasket ? 1 : isModalOpen ? 1 : 0,
          cursor: activeBasket !== item.friendlyName ? 'pointer' : 'default',
          ...style,
        }}
        onClick={handleBasketClick}
      >
        <div
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            overflowY: isModalOpen ? 'scroll' : 'hidden',
            marginRight: -16,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: isModalOpen ? 'space-between' : 'center',
              width: '100%',
              marginBottom: isModalOpen ? 18 : 6,
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'rgba(30, 30, 30, 1)',
              paddingTop: isModalOpen ? 12 : 0,
              paddingBottom: isModalOpen ? 12 : 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <p
                style={{
                  margin: 0,
                  padding: 0,
                  fontSize: isModalOpen ? 32 : 22,
                  color: colors.white,
                  marginBottom: isModalOpen ? 0 : 6,
                  marginTop: isModalOpen ? -10 : 0,
                  width: 'max-content',
                }}
              >
                {item.friendlyName}
              </p>
              {isModalOpen && (
                <p
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: 18,
                    color: colors.white,
                    marginTop: -8,
                    width: 'max-content',
                  }}
                >
                  Total: {amountOfBots} Bots | {creditsCost} Credits
                </p>
              )}
            </div>
            {isModalOpen && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  flexDirection: 'column',
                  fontSize: 14,
                  paddingRight: 16,
                  textAlign: 'end',
                }}
              >
                <InputField config={basketBalanceField} />
                <span
                  style={{
                    color: basketBalanceInputMessageColor,
                    fontWeight: 'bold',
                  }}
                >
                  {basketBalanceInputMessage}
                </span>
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              rowGap: isModalOpen ? 32 : 8,
              justifyContent: isModalOpen ? 'space-between' : 'center',
              marginLeft: isModalOpen ? 0 : 48,
              marginRight: isModalOpen ? 0 : 48,
              width: isModalOpen ? '100%' : 'auto',
              paddingBottom: isModalOpen ? 60 : 6,
            }}
          >
            {baseAssets.map((baseAsset, index) => (
              <AssetForm
                key={baseAsset}
                baseAsset={baseAsset}
                assetInfo={assetInfo[baseAsset]}
                isModalOpen={isModalOpen}
                index={index}
                basketAssetConfig={basketConfig[baseAsset]}
                handleIsIncludedChange={handleIsIncludedChange}
                handleStableBalanceChange={handleStableBalanceChange}
                handleAssetBalanceChange={handleAssetBalanceChange}
                handleIsBalanceLockedChange={handleIsBalanceLockedChange}
                handleQuoteAssetChange={handleQuoteAssetChange}
                handleExchangeChange={handleExchangeChange}
                handleLatestPriceChange={handleLatestPriceChange}
              />
            ))}
          </div>
        </div>
        {isModalOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: 'rgba(30, 30, 30, 1)',
              paddingTop: 16,
              paddingBottom: 16,
              paddingLeft: 24,
              paddingRight: 24,
              columnGap: 16,
              display: 'flex',
              borderTop: '0.5px solid rgb(68, 68, 68)',
              borderLeft: '0.5px solid rgb(68, 68, 68)',
              borderTopLeftRadius: 8,
              boxShadow: '-4px 0px -5px rgba(255, 255, 255, 0.2)',
            }}
          >
            <button
              onClick={handleDisableAllButton}
              style={{
                backgroundColor: colors.orange,
                border: `1px solid ${colors.orange}`,
                borderRadius: 4,
                cursor: 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
              }}
            >
              Disable All
            </button>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: colors.red,
                border: `1px solid ${colors.red}`,
                borderRadius: 4,
                cursor: 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              style={{
                backgroundColor: isSubscribeDisabled
                  ? colors.gray
                  : colors.green,
                border: `1px solid ${
                  isSubscribeDisabled ? colors.gray : colors.green
                }`,
                borderRadius: 4,
                cursor: isSubscribeDisabled ? 'default' : 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
              }}
              disabled={isSubscribeDisabled}
            >
              {isLoading ? (
                <LoadingIcons.Grid width={12} height={12} />
              ) : (
                'Subscribe'
              )}
            </button>
          </div>
        )}
      </div>
    );
  },
);

BasketItemContent.displayName = 'BasketItemContent';

interface AssetFormProps {
  baseAsset: string;
  assetInfo: GetTopAssetsByVolumeInfo[];
  isModalOpen: boolean | undefined;
  index: number;
  handleIsIncludedChange: (baseAsset: string, value: boolean) => void;
  handleStableBalanceChange: (baseAsset: string, value: string) => void;
  handleAssetBalanceChange: (baseAsset: string, value: string) => void;
  handleIsBalanceLockedChange: (baseAsset: string, value: boolean) => void;
  handleQuoteAssetChange: (baseAsset: string, value: string) => void;
  handleExchangeChange: (baseAsset: string, value: Exchange) => void;
  handleLatestPriceChange: (baseAsset: string, value: number) => void;
  basketAssetConfig: {
    stableBalance: string;
    assetBalance: string;
    isIncluded: boolean;
    isBalanceLocked: boolean;
    quoteAsset: string;
    exchange: Exchange;
  };
}

const AssetForm = (props: AssetFormProps) => {
  const {
    baseAsset,
    assetInfo,
    isModalOpen,
    index,
    basketAssetConfig,
    handleIsIncludedChange,
    handleStableBalanceChange,
    handleAssetBalanceChange,
    handleIsBalanceLockedChange,
    handleQuoteAssetChange,
    handleExchangeChange,
    handleLatestPriceChange,
  } = props;
  const {
    isIncluded,
    stableBalance,
    assetBalance,
    isBalanceLocked,
    quoteAsset,
    exchange,
  } = basketAssetConfig;

  const { data: spcData } = useCalculateCurrentSingleProfitCalculationQuery(
    baseAsset,
    {
      skip: !isModalOpen || !assetInfo,
    },
  );

  useEffect(() => {
    if (!spcData?.data?.savedData?.higherTNormalCandles.length) {
      return;
    }

    const latestPrice =
      spcData.data.savedData.higherTNormalCandles[
        spcData.data.savedData.higherTNormalCandles.length - 1
      ].close;
    handleLatestPriceChange(baseAsset, latestPrice);
  }, [
    baseAsset,
    handleLatestPriceChange,
    spcData?.data?.savedData.higherTNormalCandles,
  ]);

  const handleIsIncludedValueChange = useCallback(
    (value: boolean) => {
      if (!assetInfo) {
        return;
      }

      handleIsIncludedChange(baseAsset, value);
    },
    [assetInfo, baseAsset, handleIsIncludedChange],
  );

  const possibleQuoteAssets = useMemo(() => {
    // return ['USDT', 'USDC'];

    if (!assetInfo) {
      return [];
    }

    return assetInfo
      .map((o) => o.quoteAsset)
      .filter((o, i, s) => s.findIndex((so) => so === o) === i);
  }, [assetInfo]);

  const possibleExchanges = useMemo(() => {
    // return ['Binance', 'KuCoin', 'Mexc'];

    if (!assetInfo) {
      return [];
    }

    return assetInfo
      .filter((o) => o.quoteAsset === quoteAsset)
      .map((o) => o.exchange)
      .filter((o, i, s) => s.findIndex((so) => so === o) === i);
  }, [assetInfo, quoteAsset]);

  const activeAsset = useMemo(() => {
    if (!spcData?.data) {
      return false;
    }

    return spcData.data.latestOrder === 'BUY'
      ? spcData.data.baseAsset
      : spcData.data.quoteAsset;
  }, [spcData?.data]);

  const hasEnoughBalance = useMemo(() => {
    if (!spcData?.data?.savedData?.higherTNormalCandles.length) {
      return true;
    }

    const stableBalanceValue = parseInt(stableBalance);
    const assetBalanceValue = parseFloat(assetBalance);

    return (
      stableBalanceValue +
        assetBalanceValue *
          spcData.data.savedData.higherTNormalCandles[
            spcData.data.savedData.higherTNormalCandles.length - 1
          ].close >=
      MINIMUM_BOT_BALANCE
    );
  }, [
    assetBalance,
    spcData?.data?.savedData.higherTNormalCandles,
    stableBalance,
  ]);

  return (
    <div
      style={{
        maxWidth: isModalOpen ? '20%' : 'auto',
        minWidth: isModalOpen ? '14%' : 'auto',
        marginRight: isModalOpen ? 24 : 0,
      }}
    >
      <div
        style={{
          marginLeft: isModalOpen ? 0 : -12,
          zIndex: isModalOpen ? 0 : 1000 - index,
          position: 'relative',
        }}
      >
        <div style={{ position: 'relative', width: 'fit-content' }}>
          <AssetIcon
            baseAsset={baseAsset}
            width={isModalOpen ? 80 : 40}
            height={isModalOpen ? 80 : 40}
            style={isIncluded ? {} : { opacity: isModalOpen ? 0.3 : 1 }}
          />
          {isModalOpen && (
            <div style={{ position: 'absolute', bottom: 0, right: 0 }}>
              <ColoredCheckbox
                value={isIncluded}
                handleValueChange={handleIsIncludedValueChange}
                color={isIncluded ? colors.green : colors.gray}
                size={10}
              />
            </div>
          )}
        </div>
      </div>
      {isModalOpen && !!assetInfo && (
        <div>
          <div
            style={{
              display: 'flex',
              marginTop: 4,
              flexDirection: 'column',
            }}
          >
            <p
              style={{
                color: colors.gray,
                fontSize: 14,
                margin: 0,
                marginBottom: 0,
              }}
            >
              Base Asset
            </p>
            <p
              style={{
                color: colors.white,
                fontSize: 16,
                margin: 0,
                marginBottom: 0,
                marginTop: -6,
                fontWeight: 'bold',
              }}
            >
              {baseAsset}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              marginTop: 2,
              flexDirection: 'column',
            }}
          >
            <p
              style={{
                color: colors.gray,
                fontSize: 14,
                margin: 0,
                marginBottom: 4,
              }}
            >
              Quote asset:
            </p>
            <div
              style={{
                display: 'grid',
                gap: 8,
                gridTemplateColumns: 'min-content min-content',
              }}
            >
              {possibleQuoteAssets.map((possibleQuoteAsset) => (
                <Tag
                  key={possibleQuoteAsset}
                  value={possibleQuoteAsset}
                  handlePress={() =>
                    handleQuoteAssetChange(baseAsset, possibleQuoteAsset)
                  }
                  isSelected={isIncluded && quoteAsset === possibleQuoteAsset}
                />
              ))}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              marginTop: 8,
              flexDirection: 'column',
            }}
          >
            <p
              style={{
                color: colors.gray,
                fontSize: 14,
                margin: 0,
                marginBottom: 4,
              }}
            >
              Exchange:
            </p>
            <div
              style={{
                display: 'grid',
                gap: 8,
                gridTemplateColumns: 'min-content min-content',
              }}
            >
              {possibleExchanges.map((possibleExchange) => (
                <Tag
                  key={possibleExchange}
                  value={possibleExchange}
                  handlePress={() =>
                    handleExchangeChange(baseAsset, possibleExchange)
                  }
                  isSelected={isIncluded && exchange === possibleExchange}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 8,
              marginTop: 16,
            }}
          >
            <InputField
              config={{
                label: `Stable Balance (${quoteAsset})`,
                value: stableBalance,
                handleValueChange: (value: string) =>
                  handleStableBalanceChange(baseAsset, value),
                shouldFit: true,
                isLocked: !isIncluded,
                error:
                  !hasEnoughBalance && !!parseInt(stableBalance) && isIncluded
                    ? Validations.Minimum50Required
                    : undefined,
              }}
              width={120}
            />
            <InputField
              config={{
                label: `Asset Balance (${baseAsset})`,
                value: assetBalance,
                handleValueChange: (value: string) =>
                  handleAssetBalanceChange(baseAsset, value),
                shouldFit: true,
                isLocked: !isIncluded,
                error:
                  !hasEnoughBalance && !!parseFloat(assetBalance) && isIncluded
                    ? Validations.Minimum50Required
                    : undefined,
              }}
              width={120}
            />
          </div>

          <div
            style={{
              marginTop: 4,
              display: 'flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <ColoredCheckbox
              value={isBalanceLocked && isIncluded}
              handleValueChange={(value: boolean) =>
                handleIsBalanceLockedChange(baseAsset, value)
              }
              color={
                isBalanceLocked && isIncluded ? colors.orange : colors.gray
              }
              size={4}
            />
            <span
              style={{
                fontWeight: 'bold',
                color:
                  isBalanceLocked && isIncluded ? colors.orange : colors.gray,
                fontSize: 14,
                cursor: 'pointer',
              }}
              onClick={() =>
                handleIsBalanceLockedChange(baseAsset, !isBalanceLocked)
              }
            >
              Lock Balance
            </span>
          </div>

          <div>
            <span
              style={{
                fontWeight: 'bold',
                color: isIncluded ? colors.orange : colors.gray,
                fontSize: 14,
              }}
            >
              {!activeAsset
                ? 'Loading...'
                : `Active asset: ${
                    baseAsset === activeAsset ? baseAsset : quoteAsset
                  }`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasketItem;

/**
 * Things I wanted to originally show:
 * checkbox to use or not
 * stable balance I want to use
 * lock checkbox for balance
 *
 * Things I actually need to show:
 * Pair choice
 * full balance input choice
 * Show which asset is active, with a checkbox to buy/sell
 *
 * How I will show things:
 * Bigger asset icon with a checkbox in the bottom right corner, when disabled, gray out icon
 * Pair choice below that
 * Balance choice below that, with lock balance checkbox
 * Active asset
 * checkbox to buy or sell
 */
